import * as pdfjsLib from 'pdfjs-dist/build/pdf.mjs';
import Viewer from 'viewerjs';

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url
).toString();

const createPdfLightbox = () => {
    const lightbox = document.createElement('div');

    lightbox.className = 'references-pdf-lightbox';
    lightbox.setAttribute('data-references-pdf-lightbox', '');
    lightbox.setAttribute('hidden', '');

    lightbox.innerHTML = `
    <div class="references-pdf-lightbox__backdrop" data-references-pdf-close></div>
    <div class="references-pdf-lightbox__dialog" role="dialog" aria-modal="true" aria-label="Podgląd PDF">
      <div class="references-pdf-lightbox__header">
        <p class="references-pdf-lightbox__title" data-references-pdf-title>PDF</p>
        <button type="button" class="references-pdf-lightbox__close" data-references-pdf-close aria-label="Zamknij">×</button>
      </div>
      <div class="references-pdf-lightbox__content" data-references-pdf-content>
        <div class="references-pdf-lightbox__status" data-references-pdf-status>Ładowanie PDF...</div>
      </div>
    </div>
  `;

    document.body.appendChild(lightbox);

    return lightbox;
};

const clearPdfContent = (content) => {
    while (content.firstChild) {
        content.removeChild(content.firstChild);
    }
};

const renderPdf = async (src, content, status) => {
    clearPdfContent(content);
    content.appendChild(status);
    status.textContent = 'Ładowanie PDF...';

    if (!src || typeof src !== 'string') {
        throw new Error('Brak poprawnego adresu PDF.');
    }

    const loadingTask = pdfjsLib.getDocument({
        url: src
    });

    const pdf = await loadingTask.promise;

    clearPdfContent(content);

    for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
        const page = await pdf.getPage(pageNumber);
        const viewportBase = page.getViewport({ scale: 1 });

        const availableWidth = content.clientWidth > 0 ? content.clientWidth - 8 : 980;
        const maxWidth = Math.min(availableWidth, 980);
        const scale = Math.max(1, maxWidth / viewportBase.width);
        const viewport = page.getViewport({ scale });

        const pageWrap = document.createElement('div');
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');

        pageWrap.className = 'references-pdf-lightbox__page';
        canvas.className = 'references-pdf-lightbox__canvas';

        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);

        pageWrap.appendChild(canvas);
        content.appendChild(pageWrap);

        await page.render({
            canvasContext: context,
            viewport
        }).promise;
    }
};

const initReferencesPdfLightbox = () => {
    const triggers = document.querySelectorAll('[data-references-pdf-trigger]');

    if (!triggers.length) {
        return;
    }

    let lightbox = document.querySelector('[data-references-pdf-lightbox]');

    if (!lightbox) {
        lightbox = createPdfLightbox();
    }

    const title = lightbox.querySelector('[data-references-pdf-title]');
    const content = lightbox.querySelector('[data-references-pdf-content]');
    const status = lightbox.querySelector('[data-references-pdf-status]');
    const closeTriggers = lightbox.querySelectorAll('[data-references-pdf-close]');

    const open = async (src, label) => {
        if (!src) {
            return;
        }

        title.textContent = label || 'PDF';

        lightbox.removeAttribute('hidden');
        lightbox.classList.add('is-open');
        document.documentElement.classList.add('references-pdf-lightbox-is-open');

        try {
            await renderPdf(src, content, status);
        } catch (error) {
            clearPdfContent(content);
            status.textContent = 'Nie udało się załadować PDF.';
            content.appendChild(status);
            console.error('PDF.js render error:', error);
        }
    };

    const close = () => {
        lightbox.classList.remove('is-open');
        lightbox.setAttribute('hidden', '');
        document.documentElement.classList.remove('references-pdf-lightbox-is-open');

        clearPdfContent(content);
        content.appendChild(status);
        status.textContent = 'Ładowanie PDF...';
    };

    triggers.forEach((trigger) => {
        trigger.addEventListener('click', (event) => {
            event.preventDefault();

            const src = trigger.getAttribute('data-references-pdf-src');
            const label = trigger.getAttribute('data-references-pdf-title');

            open(src, label);
        });
    });

    closeTriggers.forEach((trigger) => {
        trigger.addEventListener('click', close);
    });

    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && lightbox.classList.contains('is-open')) {
            close();
        }
    });
};

const initReferencesImageViewer = () => {
    const galleries = document.querySelectorAll('[data-references-viewer]');

    if (!galleries.length) {
        return;
    }

    galleries.forEach((gallery) => {
        if (gallery.dataset.referencesViewerInitialized === 'true') {
            return;
        }

        gallery.dataset.referencesViewerInitialized = 'true';

        new Viewer(gallery, {
            inline: false,
            button: true,
            navbar: true,
            title: false,
            toolbar: {
                zoomIn: true,
                zoomOut: true,
                oneToOne: true,
                reset: true,
                prev: true,
                play: false,
                next: true,
                rotateLeft: true,
                rotateRight: true,
                flipHorizontal: false,
                flipVertical: false
            },
            tooltip: true,
            movable: true,
            zoomable: true,
            rotatable: true,
            scalable: false,
            transition: true,
            fullscreen: true,
            keyboard: true,
            url: 'data-original',
            filter(image) {
                return image.hasAttribute('data-original');
            }
        });
    });
};

const initReferencesViewer = () => {
    initReferencesImageViewer();
    initReferencesPdfLightbox();
};

export default initReferencesViewer;
